import type { ApolloCache } from '@apollo/client';
import type {
  CompleteMembershipFragment,
  GetDirectMembershipsQuery,
  GetDirectMembershipsQueryVariables,
  SupportedLocale,
} from '@frameio/graphql-client/generated';
import { GetDirectMemberships } from '@frameio/graphql-client/generated';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';
import { updateQuery } from '../updateQuery';

type DirectMembershipsResource = NonNullable<
  NonNullable<GetDirectMembershipsQuery['account']>['resource']
>;

const updateDirectMembershipsData = (
  memberships: Array<CompleteMembershipFragment | null>,
  existingMembershipsData: DirectMembershipsResource
): DirectMembershipsResource => {
  if (!existingMembershipsData.directMemberships?.nodes) {
    return existingMembershipsData;
  }

  const existingNodes = existingMembershipsData.directMemberships.nodes;
  const id = (m: {
    accountUserGroup?: { id?: string | null } | null;
    accountUser?: { id?: string | null } | null;
  }) => m.accountUserGroup?.id || m.accountUser?.id;

  const nodes = new Map<string, (typeof existingNodes)[0]>();
  existingNodes.forEach((node) => {
    const nodeId = id(node);
    if (nodeId) nodes.set(nodeId, node);
  });

  const netNew = new Set<string>();
  memberships.forEach((membership) => {
    if (!membership) return;
    const membershipId = id(membership);
    if (!membershipId) return;

    if (nodes.has(membershipId)) {
      nodes.set(membershipId, {
        ...nodes.get(membershipId),
        ...membership,
      });
      return;
    }

    netNew.add(membershipId);
    nodes.set(membershipId, membership as (typeof existingNodes)[0]);
  });

  const existingDirect = existingMembershipsData.directMemberships;
  const existingUnfiltered =
    existingMembershipsData.unfilteredDirectMembershipsTotalCount;

  if (!existingDirect || !existingUnfiltered) return existingMembershipsData;

  return {
    ...existingMembershipsData,
    directMemberships: {
      ...existingDirect,
      nodes: Array.from(nodes.values()),
      totalCount: existingDirect.totalCount + netNew.size,
      pageInfo: existingDirect.pageInfo,
    },
    unfilteredDirectMembershipsTotalCount: {
      ...existingUnfiltered,
      totalCount: existingUnfiltered.totalCount + netNew.size,
    },
  };
};

export const updateDirectMembershipsCache = (
  cache: ApolloCache<any>,
  memberships: Array<CompleteMembershipFragment | null>,
  resourceId: string,
  locale: SupportedLocale,
  accountId: string | undefined
) => {
  if (!accountId) return;

  updateQuery<GetDirectMembershipsQuery, GetDirectMembershipsQueryVariables>(
    cache,
    {
      query: GetDirectMemberships,
      variables: {
        accountId,
        locale,
        resourceId,
        page: {
          first: DEFAULT_INITIAL_PAGE_SIZE,
        },
      },
    },
    (existingMembershipsData) => {
      const resource = existingMembershipsData?.account?.resource;
      if (!existingMembershipsData?.account || !resource) {
        return existingMembershipsData;
      }

      return {
        ...existingMembershipsData,
        account: {
          ...existingMembershipsData.account,
          resource: updateDirectMembershipsData(memberships, resource),
        },
      };
    }
  );
};
